import { BrowserRouter as Router, Link, useLocation } from 'react-router-dom'
import { Disclosure } from '@headlessui/react'
import { HomeIcon, Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import Logo from './components/Logo'
import AppRoutes from './routes/AppRoutes'
import PWAUpdateNotifier from './components/PWAUpdateNotifier'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

import { AuthProvider, useAuth } from './context/AuthContext'

function Layout() {
  const location = useLocation()
  const { currentUser, logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Failed to log out', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Disclosure as="nav" className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        {({ open }) => (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  <Logo showText={true} size="lg" />
                  <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                    {!currentUser && (
                      <Link
                        to="/"
                        className={classNames(
                          location.pathname === '/'
                            ? 'border-primary-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                        )}
                      >
                        <HomeIcon
                          className={classNames(
                            location.pathname === '/' ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500',
                            'h-5 w-5 mr-1.5'
                          )}
                        />
                        Home
                      </Link>
                    )}
                    {currentUser && (
                      <Link
                        to="/boxes"
                        className={classNames(
                          location.pathname.startsWith('/boxes') && location.pathname !== '/boxes/new'
                            ? 'border-primary-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                        )}
                      >
                        My Boxes
                      </Link>
                    )}
                    {currentUser && (
                      <Link
                        to="/activity"
                        className={classNames(
                          location.pathname.startsWith('/activity')
                            ? 'border-primary-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                        )}
                      >
                        My Activity
                      </Link>
                    )}
                    <Link
                      to="/how-to"
                      className={classNames(
                        location.pathname.startsWith('/how-to')
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                      )}
                    >
                      How To
                    </Link>
                    <Link
                      to="/rules"
                      className={classNames(
                        location.pathname.startsWith('/rules')
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                      )}
                    >
                      Rules
                    </Link>
                    <Link
                      to="/versions"
                      className={classNames(
                        location.pathname.startsWith('/versions')
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                      )}
                    >
                      Versions
                    </Link>
                    {currentUser && (
                      <Link
                        to="/feedback"
                        className={classNames(
                          location.pathname.startsWith('/feedback')
                            ? 'border-primary-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors'
                        )}
                      >
                        Feedback
                      </Link>
                    )}
                  </div>
                </div>
                <div className="hidden sm:flex sm:items-center sm:space-x-4">
                  {currentUser ? (
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-700">{currentUser.email}</span>
                      <button
                        onClick={handleLogout}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <UserCircleIcon className="h-5 w-5 mr-1.5 text-gray-500" />
                      Sign In
                    </Link>
                  )}
                </div>
                <div className="-mr-2 flex items-center sm:hidden">
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="pt-2 pb-3 space-y-1">
                {!currentUser && (
                  <Disclosure.Button
                    as={Link}
                    to="/"
                    className={classNames(
                      location.pathname === '/'
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                    )}
                  >
                    Home
                  </Disclosure.Button>
                )}
                {currentUser && (
                  <Disclosure.Button
                    as={Link}
                    to="/boxes"
                    className={classNames(
                      location.pathname.startsWith('/boxes')
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                    )}
                  >
                    My Boxes
                  </Disclosure.Button>
                )}
                {currentUser && (
                  <Disclosure.Button
                    as={Link}
                    to="/activity"
                    className={classNames(
                      location.pathname.startsWith('/activity')
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                    )}
                  >
                    My Activity
                  </Disclosure.Button>
                )}
                {currentUser && (
                  <Disclosure.Button
                    as={Link}
                    to="/feedback"
                    className={classNames(
                      location.pathname.startsWith('/feedback')
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                    )}
                  >
                    Feedback
                  </Disclosure.Button>
                )}
                <Disclosure.Button
                  as={Link}
                  to="/how-to"
                  className={classNames(
                    location.pathname.startsWith('/how-to')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover-border-gray-300 hover:text-gray-700',
                    'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                  )}
                >
                  How To
                </Disclosure.Button>
                <Disclosure.Button
                  as={Link}
                  to="/rules"
                  className={classNames(
                    location.pathname.startsWith('/rules')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                    'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                  )}
                >
                  Rules
                </Disclosure.Button>
                <Disclosure.Button
                  as={Link}
                  to="/versions"
                  className={classNames(
                    location.pathname.startsWith('/versions')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                    'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
                  )}
                >
                  Versions
                </Disclosure.Button>
                <div className="pt-4 pb-3 border-t border-gray-200">
                  <div className="mt-3 space-y-1">
                    {currentUser ? (
                      <Disclosure.Button
                        as="button"
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                      >
                        Sign Out
                      </Disclosure.Button>
                    ) : (
                      <Disclosure.Button
                        as={Link}
                        to="/login"
                        className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                      >
                        Sign In
                      </Disclosure.Button>
                    )}
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <AppRoutes />
      </main>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout />
        <PWAUpdateNotifier />
      </Router>
    </AuthProvider>
  )
}

export default App
